/**
 * The partners' capital statement: a partnership's (migration 0096; T3 of
 * PLAN_tax_treatments §4.6), one fiscal year at a time.
 *
 * THE FIGURES ARE THE SERVER'S. GET /reports/partners-capital reads each
 * partner's accounts, the close's lines or the close's own split of the year
 * so far, and the balance sheet it ties to
 * (apps/api/src/services/partners-capital.ts), so this screen does no
 * arithmetic of its own.
 *
 * Its own address beside Cashflow, as the owner's equity statement is for a
 * business taxed with its owner: what Schedule K-1 item L and Form 1065's
 * Schedule M-2 report. A prompt for the CPA, never a ruling.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PrintButton, PrintHeading } from '../components/PrintReport.js';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type PartnersCapitalStatement } from '../api.js';
import { formatCents } from '../lib/money.js';

export function PartnersCapitalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const year = yearParam === null ? undefined : Number(yearParam);
  const [statement, setStatement] = useState<PartnersCapitalStatement | null>(null);
  const [current, setCurrent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getPartnersCapital(year)
      .then((result) => {
        if (!live) return;
        setStatement(result.statement);
        if (year === undefined) setCurrent(result.statement.fiscalYear);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : "Could not read the partners' capital statement.");
      });
    return () => {
      live = false;
    };
  }, [year]);

  function choose(value: string) {
    const params = new URLSearchParams(searchParams);
    params.set('year', value);
    setSearchParams(params, { replace: true });
  }

  const shown = year ?? statement?.fiscalYear;
  const latest = current ?? (shown !== undefined ? Math.max(shown, new Date().getFullYear()) : undefined);
  const choices = latest === undefined ? [] : Array.from({ length: 6 }, (_, back) => latest - back);
  if (shown !== undefined && !choices.includes(shown)) choices.push(shown);

  return (
    <section className="reports partners-capital">
      <PrintHeading covers={shown === undefined ? undefined : `Fiscal year ${shown}`} />
      <header className="row reports-head">
        <h1>Partners&apos; Capital</h1>
        <span className="muted">Each partner&apos;s capital, contributions, share of the result and draws, for one fiscal year.</span>
        <PrintButton />
      </header>

      <nav className="row tabs" aria-label="Report">
        <Link to="/reports?report=income" className="tab">
          Income Statement
        </Link>
        <Link to="/reports?report=balance" className="tab">
          Balance Sheet
        </Link>
        <Link to="/reports?report=trial" className="tab">
          Trial Balance
        </Link>
        <Link to="/reports/cashflow" className="tab">
          Cashflow
        </Link>
        <span className="tab is-current" aria-current="page">
          Partners&apos; Capital
        </span>
      </nav>

      {shown !== undefined && (
        <div className="row report-period">
          <label>
            Fiscal Year{' '}
            <select value={shown} onChange={(event) => choose(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {error === null && (statement === null || statement.fiscalYear !== shown) && <p className="muted">Loading…</p>}
      {error === null && statement !== null && statement.fiscalYear === shown && <Statement statement={statement} />}
    </section>
  );
}

function Statement({ statement }: { statement: PartnersCapitalStatement }) {
  const unassigned = statement.unassignedGuaranteedPaymentsCents;
  // Shown only when something else moved a partner's accounts (round 193's TW-1).
  const other = statement.partners.some((partner) => partner.otherChangesCents !== 0);
  return (
    <div className="report">
      <h2>Statement of Partners&apos; Capital</h2>
      <p className="muted">
        {statement.from} to {statement.to}
        {statement.yearComplete ? '' : ', the year so far'}
      </p>
      <div className="muted">
        <InfoNote label="Where These Come From">
          <p>
            Capital at the start is what each partner&apos;s three accounts held the day before the year began.
            Contributions and draws are what was posted to their Contributions and Draws accounts. Their share is the
            year-end close&apos;s line once the year is closed, and until then the shares in force applied to the
            year&apos;s result so far, split as the close would split it. Capital at the end is what their accounts
            hold, and Other Changes is whatever else moved them: an opening balance, or capital moved between
            partners.
          </p>
          <p>
            Guaranteed payments are found by the party named on each payment&apos;s entry: a partner&apos;s party, when one
            is set in Settings. They are an expense of the business, so they are shown beside the share and not added to
            capital. The total ties to the balance sheet&apos;s equity.
          </p>
        </InfoNote>
      </div>
      {statement.allocation === 'in_force' && (
        <p className="muted">The year is not closed, so each share is allocated by the shares in force, not yet closed.</p>
      )}
      {statement.allocation === 'unallocated' && statement.changedOn !== null && (
        <p className="muted">
          The partners&apos; shares changed on {statement.changedOn}, inside the year, so the year&apos;s result is not split
          here: how a result is split across a change of shares is for your CPA to choose.
        </p>
      )}
      <div className="table-scroll">
        <table className="lines">
          <thead>
            <tr>
              <th>Partner</th>
              <th className="num">Capital, {statement.from}</th>
              <th className="num">Contributions</th>
              <th className="num">Share of Result</th>
              <th className="num">Draws</th>
              {other && <th className="num">Other Changes</th>}
              <th className="num">Capital, {statement.to}</th>
              <th className="num">Guaranteed Payments</th>
            </tr>
          </thead>
          <tbody>
            {statement.partners.map((partner) => (
              <tr key={partner.partnerId}>
                <td>{partner.name}</td>
                <td className="num">{formatCents(partner.startCents)}</td>
                <td className="num">{formatCents(partner.contributionsCents)}</td>
                <td className="num">{partner.shareCents === null ? '—' : formatCents(partner.shareCents)}</td>
                <td className="num">{formatCents(-partner.drawsCents)}</td>
                {other && <td className="num">{formatCents(partner.otherChangesCents)}</td>}
                <td className="num">{formatCents(partner.endCents)}</td>
                <td className="num">{formatCents(partner.guaranteedPaymentsCents)}</td>
              </tr>
            ))}
            {statement.notYetAllocatedCents !== 0 && (
              <tr>
                <td>Not Yet Any Partner&apos;s</td>
                <td />
                <td />
                <td />
                <td />
                {other && <td />}
                <td className="num">{formatCents(statement.notYetAllocatedCents)}</td>
                <td />
              </tr>
            )}
            {unassigned !== 0 && (
              <tr>
                <td>Not Assigned To A Partner</td>
                <td />
                <td />
                <td />
                <td />
                {other && <td />}
                <td />
                <td className="num">{formatCents(unassigned)}</td>
              </tr>
            )}
            <tr className="report-total">
              <th scope="row">Total Equity, {statement.to}</th>
              <td />
              <td />
              <td />
              <td />
              {other && <td />}
              <td className="num">{formatCents(statement.totalEquityCents)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      {other && (
        <p className="muted">
          Other Changes is whatever moved a partner&apos;s accounts besides contributions, draws and the year&apos;s
          result, such as an opening balance or capital moved between partners.
        </p>
      )}
      {statement.notYetAllocatedCents !== 0 && (
        <p className="muted">
          {statement.earlierOpenYears.length > 0
            ? `Fiscal ${statement.earlierOpenYears.join(', ')} ${statement.earlierOpenYears.length === 1 ? 'is' : 'are'} not closed, so ${statement.earlierOpenYears.length === 1 ? 'its result is' : 'their results are'} no partner's yet. `
            : ''}
          {statement.allocation === 'unallocated' ? "This year's result is no partner's until it is split. " : ''}
          Closing a year splits its result across the partners.
        </p>
      )}
      {unassigned !== 0 && (
        <p className="muted">
          Some guaranteed payments name no partner&apos;s party. Set each partner&apos;s party in Settings, Partners, and
          name that party on the payment&apos;s entry, to put them in a partner&apos;s row.
        </p>
      )}
    </div>
  );
}
